'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdVpnKey } from 'react-icons/md'
import { HealthPlanSelector } from '@/components/health-plan/HealthPlanSelector'
import {
    createTissCredential,
    getTissCredential,
    updateTissCredential,
} from '@/lib/api/health-integrations/tiss-api'

/**
 * Cadastro da credencial de transmissão TISS de um convênio nesta filial.
 *
 * O convênio vem do cadastro org-global (`HealthPlanSelector`) — não se digita
 * nome nem registro ANS aqui. Na edição o convênio fica travado: trocá-lo faria
 * a credencial mudar de operadora mantendo o histórico de transmissões da
 * anterior. Para outro convênio, cadastre outra credencial.
 */
export default function TissCredentialFormPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = Boolean(id)

    const [healthPlanId, setHealthPlanId] = useState<number | null>(null)
    const [healthPlanName, setHealthPlanName] = useState('')
    const [webServiceUrl, setWebServiceUrl] = useState('')
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [active, setActive] = useState(true)
    const [loading, setLoading] = useState(isEditing)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!id) return

        const load = async () => {
            setLoading(true)
            try {
                const response = await getTissCredential(parseInt(id, 10))
                if (response.success) {
                    const credential = response.result
                    setHealthPlanId(credential.healthPlanId)
                    setHealthPlanName(credential.healthPlan?.name || '')
                    setWebServiceUrl(credential.webServiceUrl || '')
                    setUsername(credential.username || '')
                    setActive(credential.active)
                } else {
                    toast.error(
                        response.error || 'Erro ao carregar a credencial'
                    )
                }
            } catch {
                toast.error('Erro ao carregar a credencial')
            } finally {
                setLoading(false)
            }
        }

        load()
    }, [id])

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()

        if (!healthPlanId) {
            toast.error('Selecione o convênio')
            return
        }

        setSaving(true)
        try {
            const response = isEditing
                ? await updateTissCredential(parseInt(id!, 10), {
                      webServiceUrl: webServiceUrl || undefined,
                      username: username || undefined,
                      // Senha em branco na edição significa "manter a atual" —
                      // enviar string vazia apagaria a credencial gravada.
                      password: password || undefined,
                      active,
                  })
                : await createTissCredential({
                      healthPlanId,
                      webServiceUrl: webServiceUrl || undefined,
                      username: username || undefined,
                      password: password || undefined,
                  })

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Credencial atualizada com sucesso'
                        : 'Credencial cadastrada com sucesso'
                )
                navigate('/health-integrations/tiss/credentials')
            } else {
                toast.error(response.error || 'Erro ao salvar a credencial')
            }
        } catch {
            toast.error('Erro ao salvar a credencial')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando credencial...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={() =>
                        navigate('/health-integrations/tiss/credentials')
                    }
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                        <MdVpnKey className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing
                                ? 'Editar credencial'
                                : 'Nova credencial de transmissão'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Acesso ao webservice do convênio a partir desta
                            filial
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-1">
                        Convênio
                    </h2>
                    <p className="text-sm text-base-content/60 mb-5">
                        O cadastro do convênio é compartilhado por toda a
                        organização. Para criar um novo, use Convênios em
                        Cadastros.
                    </p>

                    <div className="grid grid-cols-1 gap-5">
                        {isEditing ? (
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Convênio</span>
                                </label>
                                <div className="input input-bordered flex items-center bg-base-200 opacity-70 w-full">
                                    {healthPlanName || `Convênio #${healthPlanId}`}
                                </div>
                                <span className="text-xs text-base-content/50 mt-1">
                                    O convênio não muda depois de criada a
                                    credencial. Cadastre outra credencial para
                                    outro convênio.
                                </span>
                            </div>
                        ) : (
                            <HealthPlanSelector
                                value={healthPlanId}
                                onChange={setHealthPlanId}
                                label="Convênio *"
                                placeholder="Buscar convênio..."
                            />
                        )}
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-1">
                        Acesso ao webservice
                    </h2>
                    <p className="text-sm text-base-content/60 mb-5">
                        Dados fornecidos pela operadora. A senha é gravada
                        criptografada e nunca é exibida de volta.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text">
                                    URL do webservice
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="https://webservice.operadora.com.br/tiss"
                                value={webServiceUrl}
                                onChange={e => setWebServiceUrl(e.target.value)}
                                autoComplete="off"
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Usuário</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Usuário fornecido pela operadora"
                                value={username}
                                onChange={e => setUsername(e.target.value)}
                                autoComplete="off"
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Senha</span>
                            </label>
                            <input
                                type="password"
                                className="input input-bordered w-full"
                                placeholder={
                                    isEditing
                                        ? 'Deixe em branco para manter a atual'
                                        : 'Senha fornecida pela operadora'
                                }
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                autoComplete="new-password"
                            />
                        </div>

                        {isEditing && (
                            <div className="form-control w-full md:col-span-2">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary"
                                        checked={active}
                                        onChange={e =>
                                            setActive(e.target.checked)
                                        }
                                    />
                                    <span className="label-text">
                                        Credencial ativa
                                    </span>
                                </label>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() =>
                            navigate('/health-integrations/tiss/credentials')
                        }
                        disabled={saving}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        Salvar
                    </button>
                </div>
            </form>
        </div>
    )
}
